import ParameterValidator from '@mvps-genesis/parameter-validator';

/*
 *--------------------------------------------------------------------------
 * LoadingUI
 *--------------------------------------------------------------------------
 *
 * Class for showing and hiding a loading state UI.
 *
 */
export default class LoadingUI {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * The target loading element
	 *
	 * @var	{HTMLElement}
	 */
	loadingElement;

	/**
	 * The loading class name to apply to the target loading element
	 *
	 * @var	{String}
	 */
	loadingClass;

	/**
	 * The target element for the background overlay
	 *
	 * @var	{HTMLElement|Boolean}
	 */
	overlayElement;

	/**
	 * The overlay class name to apply to the target overylay element
	 *
	 * @var	{String}
	 */
	overlayClass;

	/**
	 * Flag to determine if the target loading element is hidden on page load
	 *
	 * @var	{Boolean}
	 */
	hideOnLoad;

	/**
	 * Create a LoadingUI instance
	 *
	 * @param  {HTMLElement|String}  loadingElement                  The target loading element.
	 * @param  {Boolean}  options.hideOnLoad                         Flag to determine if the target loading element is hidden on page load. Defaults to true.
	 * @param  {String}  options.loadingClass                        The loading class name to apply to the target loading element. Defaults to 'g-loading'.
	 * @param  {String}  options.overlayClass                        The overlay class name to apply to the target overylay element. Defaults to 'g-loading-overlay'.
	 * @param  {HTMLElement|String|Boolean}  options.overlayElement  The target element for the background overlay. A value set to false disables the overlay element functionality. Defaults to false.
	 */
	constructor (loadingElement, {
		hideOnLoad = true,
		loadingClass = 'g-loading',
		overlayClass = 'g-loading-overlay',
		overlayElement = false
	} = {}) {
		this.#validator = new ParameterValidator(this);
		this.loadingElement = this.#validator.isElement('loadingElement', loadingElement);
		this.loadingClass = this.#validator.isString('loadingClass', loadingClass);
		this.overlayClass = this.#validator.isString('overlayClass', overlayClass);
		this.overlayElement = overlayElement;

		if (this.overlayElement !== false) {
			this.overlayElement = this.#validator.isElement('overlayElement', overlayElement);
		}

		this.hideOnLoad = this.#validator.isBoolean('hideOnLoad', hideOnLoad);

		this.init();
	}

	/**
	 * Class initialization tasks
	 */
	init () {
		this.loadingElement.classList.add(this.loadingClass);

		if (this.hideOnLoad) {
			this.loadingElement.classList.add('u-hidden');
		}
	}

	/**
	 * Hide the loading and overlay elements
	 *
	 * @return {this}
	 */
	hide () {
		this.loadingElement.classList.add('u-hidden');

		if (this.overlayElement) {
			this.overlayElement.classList.remove(this.overlayClass);
		}

		return this;
	}

	/**
	 * Show the loading and overlay elements
	 *
	 * @return {this}
	 */
	show () {
		if (this.overlayElement) {
			this.overlayElement.classList.add(this.overlayClass);
		}

		this.loadingElement.classList.remove('u-hidden');

		return this;
	}
};
